import { useEffect, useRef, useState } from "react";
import { navItems, hotSearches } from "@/mocks/home";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchFocus, setSearchFocus] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [noticeVisible, setNoticeVisible] = useState(true);
  const [hint, setHint] = useState("");
  const hintTimer = useRef<number | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const showHint = (message: string) => {
    setHint(message);
    if (hintTimer.current) window.clearTimeout(hintTimer.current);
    hintTimer.current = window.setTimeout(() => setHint(""), 2400);
  };

  return (
    <header id="top" className="sticky top-0 z-50 w-full">
      {noticeVisible && (
        <div className="w-full bg-primary-950 text-primary-100">
          <div className="mx-auto flex h-9 max-w-[1280px] items-center gap-3 px-4 text-xs md:px-6">
            <span className="flex items-center gap-1.5 font-medium text-primary-200">
              <i className="ri-megaphone-line text-accent-300" />
              78动漫 App 4.0 上线
            </span>
            <span className="hidden text-primary-200/80 sm:block">
              新增扫码识款 · 收藏跨端同步 · 到货自动提醒
            </span>
            <a
              href="#download"
              className="ml-auto flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-400"
            >
              立即下载
              <i className="ri-arrow-right-line" />
            </a>
            <button
              type="button"
              aria-label="关闭通知"
              onClick={() => setNoticeVisible(false)}
              className="flex h-5 w-5 cursor-pointer items-center justify-center text-primary-200/70 transition-colors hover:text-primary-100"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>
      )}

      <div
        className={`w-full border-b transition-all duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-6 px-4 md:px-6">
          <a href="#top" className="flex items-center gap-2 whitespace-nowrap">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-vip-diamond-line text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <strong className="font-heading text-lg font-bold text-foreground-950">78动漫</strong>
              <span className="text-[10px] tracking-widest text-foreground-400">MODEL &amp; FIGURE</span>
            </span>
          </a>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="relative ml-auto hidden max-w-xs flex-1 md:block lg:max-w-sm">
            <i className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 ri-search-line" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onFocus={() => setSearchFocus(true)}
              onBlur={() => window.setTimeout(() => setSearchFocus(false), 150)}
              placeholder="搜索手办 / 系列 / 厂商"
              className="h-9 w-full rounded-full border border-background-300 bg-background-100 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
            />
            {searchFocus && (
              <div className="absolute left-0 right-0 top-11 rounded-lg border border-background-200 bg-background-50 p-3">
                <p className="mb-2 text-xs text-foreground-400">大家都在搜</p>
                <div className="flex flex-wrap gap-2">
                  {hotSearches.map((word) => (
                    <button
                      key={word}
                      type="button"
                      onMouseDown={() => setKeyword(word)}
                      className="cursor-pointer rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 md:ml-0">
            <button
              type="button"
              onClick={() => showHint("演示站点 · 暂不开放登录")}
              className="hidden whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:text-primary-700 md:block"
            >
              登录
            </button>
            <button
              type="button"
              onClick={() => showHint("演示站点 · 注册暂未开放")}
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:block"
            >
              加入社区
            </button>
            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                showHint("演示站点 · 注册暂未开放");
              }}
              className="mt-2 w-full whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
            >
              加入社区
            </button>
          </div>
        )}
      </div>

      {hint && (
        <div className="pointer-events-none fixed left-1/2 top-24 z-[60] -translate-x-1/2 rounded-full bg-foreground-950 px-4 py-2 text-xs text-background-50">
          {hint}
        </div>
      )}
    </header>
  );
}